import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { NotificationProps } from '../../context/NotificationContext';
import { LanguageContext } from '../../App';
interface AlertProps extends Omit<NotificationProps, 'variant'> {
  onDismiss: () => void;
}
export const Alert: React.FC<AlertProps> = ({
  title,
  message,
  type = 'info',
  onDismiss,
  onAction,
  actionText,
  isOpen = true
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5" data-id="element-720" />;
      case 'error':
        return <AlertCircleIcon className="w-5 h-5" data-id="element-721" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5" data-id="element-722" />;
      case 'info':
        return <InfoIcon className="w-5 h-5" data-id="element-723" />;
      default:
        return null;
    }
  };
  const getStyles = (): {
    bg: string;
    border: string;
    text: string;
    icon: string;
    button: string;
  } => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          text: 'text-green-800 dark:text-green-200',
          icon: 'text-green-500 dark:text-green-400',
          button: 'bg-green-500 hover:bg-green-600 text-white'
        };
      case 'error':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-800 dark:text-red-200',
          icon: 'text-red-500 dark:text-red-400',
          button: 'bg-red-500 hover:bg-red-600 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          text: 'text-yellow-800 dark:text-yellow-200',
          icon: 'text-yellow-500 dark:text-yellow-400',
          button: 'bg-yellow-500 hover:bg-yellow-600 text-white'
        };
      case 'info':
        return {
          bg: 'bg-congress-blue-50 dark:bg-congress-blue-900/20',
          border: 'border-congress-blue-200 dark:border-congress-blue-800',
          text: 'text-congress-blue-800 dark:text-congress-blue-200',
          icon: 'text-congress-blue-500 dark:text-congress-blue-400',
          button: 'bg-congress-blue-500 hover:bg-congress-blue-600 text-white'
        };
      case 'neutral':
        return {
          bg: 'bg-gray-50 dark:bg-gray-800/50',
          border: 'border-gray-200 dark:border-gray-700',
          text: 'text-gray-800 dark:text-gray-200',
          icon: 'text-gray-500 dark:text-gray-400',
          button: 'bg-gray-500 hover:bg-gray-600 text-white'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800/50',
          border: 'border-gray-200 dark:border-gray-700',
          text: 'text-gray-800 dark:text-gray-200',
          icon: 'text-gray-500 dark:text-gray-400',
          button: 'bg-gray-500 hover:bg-gray-600 text-white'
        };
    }
  };
  const styles = getStyles();
  if (!isOpen) return null;
  return <motion.div className={`${styles.bg} border ${styles.border} rounded-md shadow-sm pointer-events-auto max-w-md w-full overflow-hidden`} initial={{
    opacity: 0,
    y: -20,
    scale: 0.95
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -20,
    scale: 0.95
  }} transition={{
    duration: 0.2
  }} data-id="element-724">
      <div className="p-4" data-id="element-725">
        <div className="flex items-center justify-between" data-id="element-726">
          <div className="flex items-center" data-id="element-727">
            <span className={`mr-3 ${styles.icon}`} data-id="element-728">{getIcon()}</span>
            <p className={`font-medium ${styles.text}`} data-id="element-729">{title}</p>
          </div>
          <button onClick={onDismiss} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 rounded-full p-1" aria-label={language === 'en' ? 'Close' : 'إغلاق'} data-id="element-730">
            <XIcon className="w-4 h-4" data-id="element-731" />
          </button>
        </div>
        {message && <p className={`mt-2 text-sm ${styles.text} opacity-90`} data-id="element-732">{message}</p>}
        {actionText && onAction && <div className="mt-3" data-id="element-733">
            <button onClick={onAction} className={`px-4 py-2 text-sm font-medium rounded-md ${styles.button} transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 focus:ring-current`} data-id="element-734">
              {actionText}
            </button>
          </div>}
      </div>
    </motion.div>;
};